import { IHeaderGroupAngularComp } from '@ag-grid-community/angular';
import { Column, IHeaderGroupParams } from '@ag-grid-community/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';

interface AgHeaderDropdownComponentParams extends IHeaderGroupParams {
  expandAll?: boolean;
  iconClass?: string;
  toggleCb?: (name: string, column: Column[], params: { hide: boolean }) => void;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule, NgClass, IconComponent, CheckboxComponent, AsyncPipe],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <div class="flex items-center justify-center space-x-2">
      <span>{{ label }}</span>
      <button class="aux-link flex items-center no-underline" (click)="openList()">
        <aux-icon [name]="(change$ | async) ? 'ChevronUp' : 'ChevronDown'" [class]="iconClass" />
      </button>
      <div>
        <div #trigger="cdkOverlayOrigin" class="mt-12" cdkOverlayOrigin></div>
      </div>
    </div>

    <ng-template
      cdkConnectedOverlay
      class="border-solid border-aux-gray-dark-100"
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="isOpen"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="'ttt'"
      (backdropClick)="closeList()"
      (detach)="closeList()"
    >
      <div class="border border-aux-gray-light bg-white py-1.5 shadow">
        @for (item of children || []; track item; let index = $index) {
          <div
            class="relative flex cursor-pointer select-none px-2.5 py-1.5 text-xs text-gray-900"
            [ngClass]="{
              'bg-aux-gray-light': item.getId() === highlightedOption,
            }"
            (mouseenter)="highlightOption(item.getId())"
            (mouseleave)="highlightedOption = null"
          >
            <aux-checkbox
              [checked]="isVisible[item.getId()]"
              [disabled]="isOnlyVisible(item.getId())"
              (customChange)="toggleChildren(item.getId())"
            />
            {{ item.getColDef().headerName }}
          </div>
        }
      </div>
    </ng-template>
  `,
})
export class AgHeaderDropdownComponent implements IHeaderGroupAngularComp {
  change$ = new BehaviorSubject(false);
  children!: Column[];
  column!: Column;
  highlightedOption: null | string = null;
  iconClass = 'text-white';
  isOpen = false;
  isVisible!: Record<string, boolean>;
  label!: string;
  params!: AgHeaderDropdownComponentParams;

  agInit(params: AgHeaderDropdownComponentParams): void {
    this.params = params;
    this.iconClass = params.iconClass ? params.iconClass : this.iconClass;
    this.label = params.displayName;
    this.children = this.params?.columnGroup.getProvidedColumnGroup().getChildren() as Column[];
    this.isVisible = this.children.reduce(
      (acc, el) => {
        acc[el.getId()] = el.isVisible();

        return acc;
      },
      {} as Record<string, boolean>
    );
  }

  closeList() {
    this.isOpen = false;
  }

  highlightOption(id: string): void {
    this.highlightedOption = id;
  }

  isOnlyVisible(id: string) {
    return this.children.every((c) => !c.isVisible() || c.getId() === id);
  }

  openList() {
    this.isOpen = true;
  }

  refresh(): boolean {
    return false;
  }

  toggleChildren(id: string) {
    const column = this.params?.columnGroup
      .getProvidedColumnGroup()
      .getChildren()
      .find((el) => id === el.getId());
    const isVisible = column?.isVisible();
    const columnField = (column as Column).getColDef().field || '';

    this.params?.api.setColumnsVisible([columnField], !isVisible);
    this.isVisible[id] = !!column?.isVisible();

    if (this.params?.toggleCb) {
      this.params.toggleCb(this.params.displayName, [column as Column], {
        hide: !this.isVisible[id],
      });
    }
  }
}
